// Política de Privacidad - Privacy Policy (RGPD/GDPR Compliant)
import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPolicy = () => {
    return (
        <div className="legal-container">
            <nav className="legal-nav">
                <Link to="/" className="legal-back">← Volver al inicio</Link>
                <Link to="/" className="legal-logo">
                    <div className="legal-logo-icon">⚡</div>
                    <span className="legal-logo-text">Roadmap<span>Platform</span></span>
                </Link>
            </nav>

            <header className="legal-header">
                <h1>Política de Privacidad</h1>
                <p className="legal-subtitle">Cómo protegemos y tratamos tus datos personales</p>
                <span className="legal-updated">Última actualización: Febrero 2026</span>
            </header>

            <main className="legal-content">
                <section className="legal-section">
                    <h2>1. Información general</h2>
                    <p>En Roadmap Platform ("nosotros", "nuestro", "la Plataforma"), respetamos tu privacidad y nos comprometemos a proteger tus datos personales conforme al Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica 3/2018 de Protección de Datos.</p>
                    
                    <div className="legal-contact-card">
                        <h4>Responsable del tratamiento</h4>
                        <p className="legal-contact-intro">Roadmap Platform es un servicio del ecosistema FIXARR. Para el ejercicio de derechos y consultas sobre datos personales puede dirigirse a:</p>
                        <div className="contact-item">
                            <span className="contact-icon">🏢</span>
                            <div className="contact-info">
                                <span className="contact-label">Identidad</span>
                                <span className="contact-value">FIXARR / titular del dominio roadmap.fixarr.es</span>
                            </div>
                        </div>
                        <div className="contact-item">
                            <span className="contact-icon">🆔</span>
                            <div className="contact-info">
                                <span className="contact-label">NIF/CIF</span>
                                <span className="contact-value">A completar por el titular del proyecto (NIF/CIF del responsable legal)</span>
                            </div>
                        </div>
                        <div className="contact-item">
                            <span className="contact-icon">📍</span>
                            <div className="contact-info">
                                <span className="contact-label">Dirección</span>
                                <span className="contact-value">España (domicilio fiscal del responsable; actualizar en Aviso Legal)</span>
                            </div>
                        </div>
                        <div className="contact-item">
                            <span className="contact-icon">📧</span>
                            <div className="contact-info">
                                <span className="contact-label">Email de privacidad</span>
                                <span className="contact-value">privacidad@roadmap.fixarr.es</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="legal-section">
                    <h2>2. Datos que recopilamos</h2>
                    
                    <h3>2.1 Datos proporcionados por ti</h3>
                    <ul>
                        <li><strong>Datos de registro (vía GitHub):</strong> Nombre de usuario, email, avatar, ID de GitHub</li>
                        <li><strong>Datos de proyectos:</strong> Roadmaps, tareas, descripciones, estados y configuraciones</li>
                        <li><strong>Credenciales de servicios externos:</strong> API keys de Supabase, tokens (cifrados)</li>
                        <li><strong>Datos de contacto:</strong> Información proporcionada al contactarnos</li>
                    </ul>

                    <h3>2.2 Datos recopilados automáticamente</h3>
                    <ul>
                        <li><strong>Datos de uso:</strong> Páginas visitadas, acciones realizadas, tiempo de sesión</li>
                        <li><strong>Datos técnicos:</strong> Dirección IP, navegador, dispositivo, sistema operativo</li>
                        <li><strong>Cookies:</strong> Ver nuestra <Link to="/cookies">Política de Cookies</Link></li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>3. Base legal del tratamiento</h2>
                    <p>Tratamos tus datos basándonos en las siguientes bases legales:</p>
                    
                    <table className="legal-table">
                        <thead>
                            <tr>
                                <th>Finalidad</th>
                                <th>Base Legal</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Prestación del servicio</td>
                                <td>Ejecución del contrato (art. 6.1.b RGPD)</td>
                            </tr>
                            <tr>
                                <td>Gestión de pagos</td>
                                <td>Ejecución del contrato (art. 6.1.b RGPD)</td>
                            </tr>
                            <tr>
                                <td>Comunicaciones de servicio</td>
                                <td>Interés legítimo (art. 6.1.f RGPD)</td>
                            </tr>
                            <tr>
                                <td>Mejora del servicio</td>
                                <td>Interés legítimo (art. 6.1.f RGPD)</td>
                            </tr>
                            <tr>
                                <td>Cumplimiento legal</td>
                                <td>Obligación legal (art. 6.1.c RGPD)</td>
                            </tr>
                            <tr>
                                <td>Cookies no esenciales</td>
                                <td>Consentimiento (art. 6.1.a RGPD)</td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                <section className="legal-section">
                    <h2>4. Seguridad de los datos</h2>
                    <p>Implementamos medidas técnicas y organizativas de seguridad:</p>
                    
                    <h3>4.1 Medidas generales</h3>
                    <ul>
                        <li><strong>Cifrado en tránsito:</strong> Todas las comunicaciones usan HTTPS/TLS 1.3</li>
                        <li><strong>Cifrado en reposo:</strong> Base de datos cifrada con AES-256</li>
                        <li><strong>Autenticación segura:</strong> OAuth 2.0 con GitHub (no almacenamos contraseñas)</li>
                        <li><strong>Control de acceso:</strong> Row Level Security (RLS) en base de datos</li>
                        <li><strong>Backups cifrados:</strong> Copias de seguridad automáticas diarias</li>
                    </ul>

                    <h3>4.2 Protección de credenciales sensibles (Vault)</h3>
                    <div className="legal-info-box">
                        <p><strong>Máxima seguridad para tus API keys:</strong></p>
                        <ul style={{ margin: '12px 0 0', paddingLeft: '20px' }}>
                            <li>Las credenciales se cifran con <strong>AES-256-GCM</strong> antes de almacenarse</li>
                            <li>Cada usuario tiene una clave de cifrado única derivada</li>
                            <li>Las claves nunca se almacenan en texto plano</li>
                            <li>Solo TÚ puedes descifrar tus propias credenciales</li>
                            <li>Ni siquiera nosotros podemos ver tus API keys</li>
                        </ul>
                    </div>

                    <div className="legal-info-box warning">
                        <p><strong>Importante:</strong> Aunque implementamos seguridad de nivel bancario, te recomendamos no almacenar credenciales de producción críticas. El Vault está diseñado principalmente para desarrollo.</p>
                    </div>
                </section>

                <section className="legal-section">
                    <h2>5. Compartición de datos</h2>
                    <p><strong>No vendemos tus datos personales.</strong> Solo compartimos información con:</p>
                    
                    <h3>5.1 Proveedores de servicio</h3>
                    <table className="legal-table">
                        <thead>
                            <tr>
                                <th>Proveedor</th>
                                <th>Propósito</th>
                                <th>Ubicación</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Supabase</td>
                                <td>Base de datos y autenticación</td>
                                <td>UE (Alemania)</td>
                            </tr>
                            <tr>
                                <td>Vercel</td>
                                <td>Hosting de la aplicación</td>
                                <td>UE</td>
                            </tr>
                            <tr>
                                <td>GitHub</td>
                                <td>Autenticación OAuth</td>
                                <td>EE.UU. (DPF)*</td>
                            </tr>
                            <tr>
                                <td>Stripe</td>
                                <td>Procesamiento de pagos</td>
                                <td>UE</td>
                            </tr>
                        </tbody>
                    </table>
                    <p style={{ fontSize: '0.9rem', color: '#666' }}>* GitHub está adherido al Data Privacy Framework UE-EE.UU.</p>

                    <h3>5.2 Autoridades</h3>
                    <p>Podemos compartir datos cuando sea requerido por ley o autoridad judicial competente.</p>
                </section>

                <section className="legal-section">
                    <h2>6. Tus derechos (RGPD)</h2>
                    <p>Tienes los siguientes derechos sobre tus datos personales:</p>
                    
                    <ul>
                        <li><strong>Acceso:</strong> Solicitar una copia de todos tus datos personales</li>
                        <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos</li>
                        <li><strong>Supresión:</strong> Solicitar la eliminación de tus datos ("derecho al olvido")</li>
                        <li><strong>Portabilidad:</strong> Recibir tus datos en formato estructurado (JSON)</li>
                        <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos</li>
                        <li><strong>Limitación:</strong> Solicitar que limitemos el tratamiento</li>
                        <li><strong>Retirar consentimiento:</strong> En cualquier momento, sin efecto retroactivo</li>
                    </ul>

                    <div className="legal-contact-card">
                        <h4>Cómo ejercer tus derechos</h4>
                        <p style={{ margin: '8px 0', color: '#666' }}>Puedes ejercer tus derechos de varias formas:</p>
                        <ul style={{ margin: '8px 0', paddingLeft: '20px', color: '#666' }}>
                            <li>Desde tu panel de Configuración (eliminación de cuenta)</li>
                            <li>Email a: <strong>privacidad@roadmap.fixarr.es</strong></li>
                            <li>Formulario de <Link to="/contact">contacto</Link></li>
                        </ul>
                        <p style={{ margin: '8px 0', color: '#666' }}>Responderemos en un plazo máximo de 30 días.</p>
                    </div>

                    <p>También puedes presentar una reclamación ante la <strong>Agencia Española de Protección de Datos (AEPD)</strong> si consideras vulnerados tus derechos: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a></p>
                </section>

                <section className="legal-section">
                    <h2>7. Retención de datos</h2>
                    <ul>
                        <li><strong>Cuenta activa:</strong> Mientras mantengas tu cuenta</li>
                        <li><strong>Tras cancelación:</strong> 30 días (periodo de gracia para reactivación)</li>
                        <li><strong>Datos de facturación:</strong> 5 años (obligación fiscal)</li>
                        <li><strong>Logs de seguridad:</strong> 12 meses</li>
                    </ul>
                    <p>Puedes solicitar la eliminación inmediata de tus datos (excepto los requeridos por ley).</p>
                </section>

                <section className="legal-section">
                    <h2>8. Transferencias internacionales</h2>
                    <p>Tus datos se almacenan principalmente en la Unión Europea. Cuando utilizamos proveedores fuera de la UE:</p>
                    <ul>
                        <li>Verificamos que estén adheridos al Data Privacy Framework UE-EE.UU.</li>
                        <li>O que cuenten con Cláusulas Contractuales Tipo aprobadas por la UE</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>9. Menores de edad</h2>
                    <p>Este servicio no está dirigido a menores de 16 años. No recopilamos conscientemente datos de menores. Si eres padre/madre/tutor y crees que un menor nos ha proporcionado datos, contacta con nosotros para su eliminación inmediata.</p>
                </section>

                <section className="legal-section">
                    <h2>10. Cambios en esta política</h2>
                    <p>Podemos actualizar esta política periódicamente. Te notificaremos cambios significativos mediante:</p>
                    <ul>
                        <li>Email a la dirección asociada a tu cuenta</li>
                        <li>Aviso destacado en la plataforma</li>
                    </ul>
                    <p>La fecha de "última actualización" indica cuándo se realizaron los últimos cambios.</p>
                </section>

                <section className="legal-section">
                    <h2>11. Contacto</h2>
                    <p>Para cualquier consulta sobre privacidad:</p>
                    <ul>
                        <li><strong>Email de privacidad:</strong> privacidad@roadmap.fixarr.es</li>
                        <li><strong>Formulario:</strong> <Link to="/contact">roadmap.fixarr.es/contact</Link></li>
                    </ul>
                </section>
            </main>

            <footer className="legal-footer">
                <div className="legal-footer-links">
                    <Link to="/legal">Aviso Legal</Link>
                    <Link to="/cookies">Política de Cookies</Link>
                    <Link to="/terms">Términos y Condiciones</Link>
                    <Link to="/contact">Contacto</Link>
                </div>
                <p>© 2026 Roadmap Platform. Parte del ecosistema FIXARR.</p>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;
